import {
  Button,
  Card,
  Dropdown,
  Flex,
  Input,
  MenuProps,
  Space,
  Spin,
  Table,
  TableColumnsType,
  Typography
} from "antd";
import { ItemType } from "antd/es/menu/interface";
import { getContactsForAccount } from "api/services/ContactService";
import { TContact } from "api/services/models/types";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import { AxiosError } from "axios";
import ErrorViewer from "components/ErrorViewer";
import { ReactComponent as AddSquareIcon } from "Icons/vuesax/add-square.svg";
import { ReactComponent as ViewDictationInstructionsIcon } from "Icons/vuesax/document.svg";
import { ReactComponent as EditIcon } from "Icons/vuesax/edit-2.svg";
import { ReactComponent as MoreCircleIcon } from "Icons/vuesax/more-circle.svg";
import { ReactComponent as EditVTUserIcon } from "Icons/vuesax/musicnote.svg";
import { ReactComponent as ProviderIcon } from "Icons/vuesax/user-octagon.svg";
import { ReactComponent as ContactIcon } from "Icons/vuesax/user.svg";

import { WebDPrivilege } from "api/services/models/WebDUser";
import { useEffect, useState } from "react";
import AddOrUpdateProvider from "./AddOrUpdateProvider";
import AddOrUpdateVTUser from "./AddOrUpdateVTUsers";
import DictationInstructions from "./DictationInstructions";
import { usePicklists } from "components/picklists/PicklistContextProvider";

type TContactActions = {
  onEditContact: (contact: TContact) => void;
  onEditVTUser: (contact: TContact) => void;
  onViewDictationInstructions: (contact: TContact) => void;
};

const buildActionsMenu = (
  contact: TContact,
  actions: TContactActions,
  hasManageVTUsers: boolean,
  hasViewDictationInstructions: boolean,
  hasViewDictationInstructionsLocation: boolean
): MenuProps => {
  const items: ItemType[] = [
    {
      key: "editContact",
      icon: <EditIcon className="icon" />,
      onClick: () => actions.onEditContact(contact),
      label: "Edit Contact",
      className: "user-context-menu"
    }
  ];

  if (contact.isProvider && hasManageVTUsers) {
    items.push({
      key: "editVTUser",
      icon: <EditVTUserIcon className="icon" />,
      onClick: () => actions.onEditVTUser(contact),
      label: contact.isVTUser ? "Edit Dictation User" : "Create Dictation User",
      className: "user-context-menu"
    });
  }

  if (
    contact.isProvider &&
    contact.isVTUser &&
    hasViewDictationInstructions &&
    hasViewDictationInstructionsLocation
  ) {
    items.push({
      key: "viewDictationInstructions",
      icon: <ViewDictationInstructionsIcon className="icon" />,
      onClick: () => actions.onViewDictationInstructions(contact),
      label: "View Dictation Instructions",
      className: "user-context-menu"
    });
  }

  return { items };
};

const ManageContacts = () => {
  const { accountId, webDUser } = useTDAuthentication();
  const { refresh } = usePicklists();
  const hasManageVTUsers: boolean =
    webDUser?.hasPrivilege(WebDPrivilege.ManageVTUsers) ?? false;
  const hasViewDictationInstructions: boolean =
    webDUser?.hasPrivilege(WebDPrivilege.ViewDictationInstructions) ?? false;
  const hasViewDictationInstructionsLocation: boolean =
    webDUser?.hasPrivilege(WebDPrivilege.ManageLocations) ?? false;
  const [contacts, setContacts] = useState<TContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | AxiosError | null>();
  const [editContactState, setEditContactState] = useState<{
    showModal: boolean;
    contact?: TContact;
  }>();
  const [editVTUserState, setEditVTUserState] = useState<{
    showModal: boolean;
    contact?: TContact;
  }>();
  const [viewDictationInstructions, setViewDictationInstructions] = useState<{
    showModal: boolean;
    contact?: TContact;
  }>();

  const [tableKey, setTableKey] = useState(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  
  const composeColumns = (
    actions: TContactActions,
    hasManageVTUsers: boolean,
    hasViewDictationInstructions: boolean,
    hasViewDictationInstructionsLocation: boolean): TableColumnsType<TContact> => [
    {
      title: "",
      filters: [
        { text: "Providers", value: true },
        { text: "Contacts", value: false }
      ],
      onFilter: (value, user) => user.isProvider === value,
      render: (_: any, record: TContact) =>
        record.isProvider ? (
          <ProviderIcon className="provider-icon"></ProviderIcon>
        ) : (
          <ContactIcon className="contact-icon"></ContactIcon>
        ),
    },
    {
      title: "Name",
      dataIndex: "contactName",
      sorter: (s1, s2) => s1.contactName?.localeCompare(s2.contactName),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
        <div style={{ padding: 8 }} onKeyDown={e => e.stopPropagation()} role="none">
          <Input
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              onClick={() => clearFilters?.({ closeDropdown: true, confirm: true })}
              size="small"
              style={{ width: 90 }}>
              Reset
            </Button>
            <Button
              type="primary"
              onClick={() => confirm({ closeDropdown: true })}
              size="small"
              style={{ width: 90 }}>
              Search
            </Button>
          </Space>
        </div>
      ),
      filterSearch: true,
      filterDropdownProps: {
        placement: "bottom",
      },
      onFilter: (value, record) =>
        record.contactName.toLowerCase().includes((value as string).toLowerCase())
    },
    {
      title: "Phone",
      dataIndex: "phone"
    },
    {
      title: "Email",
      dataIndex: "email"
    },
    {
      title: "Primary Location",
      dataIndex: "primaryLocation"
    },
    {
      title: "Primary Region",
      dataIndex: "primaryRegion"
    },
    {
      title: "",
      render: (_: any, record: TContact) => (
        <Flex justify="center" align="center">
          <Dropdown
            menu={buildActionsMenu(
              record,
              actions,
              hasManageVTUsers,
              hasViewDictationInstructions,
              hasViewDictationInstructionsLocation
            )}
            trigger={["click", "contextMenu"]}
            placement="bottomLeft"
            className="user-context-menu">
            <MoreCircleIcon
              width="16px"
              height="16px"
              style={{ cursor: "pointer", textAlign: "center" }}
            />
          </Dropdown>
        </Flex>
      )
    }
  ];

  useEffect(() => {
    if (accountId) {
      setLoading(true);
      setError(null);
      getContactsForAccount(accountId)
        .then(setContacts)
        .then(() => setTableKey(k => k + 1))
        .catch(setError)
        .finally(() => setLoading(false));
    }
  }, [accountId]);

  const closeEditContactModal = (contact?: TContact) => {
    setEditContactState({ showModal: false });
    if (contact) {
      setContacts(cs => {
        const index = cs.findIndex(c => c.contactId === contact.contactId);
        if (index === -1) {
          cs?.push(contact);
        } else {
          cs?.splice(index, 1, contact);
        }
        return [...cs];
      });
      refresh?.("Providers");
      refresh?.("Contacts");
    }
  };

  const onEditContact = (contact?: TContact) =>
    setEditContactState({ showModal: true, contact });

  const closeEditVTUserModal = (vtUserId?: string) => {
    if (vtUserId && editVTUserState?.contact) {
      setContacts(contacts =>
        contacts?.map(c =>
          c.contactId === editVTUserState.contact?.contactId ? { ...c, isVTUser: true } : c
        )
      );
    }
    setEditVTUserState({ showModal: false });
  };
  const onEditVTUser = (contact?: TContact) => setEditVTUserState({ showModal: true, contact });

  const closeViewDictationInstructionsModal = () => {
    setViewDictationInstructions({ showModal: false });
  };

  const onViewDictationInstructions = (contact?: TContact) =>
    setViewDictationInstructions({ showModal: true, contact });

  return (
    <Card
      className="default-border"
      styles={{ body: { padding: "0" } }}
      style={{
        padding: "16px"
      }}>
      <Flex wrap gap="middle" align="start" justify="space-between" style={{ marginBottom: "16px" }}>
        <Typography.Title style={{ fontSize: "1rem" }} className="font-1212">
          Manage Providers/Contacts
        </Typography.Title>
        <Space size="small" direction="vertical" style={{ margin: 0 }} align="end">
          <Button
            type="primary"
            size="middle"
            className="default-button primary-btn"
            icon={<AddSquareIcon className="default-icon" />}
            iconPosition={"start"}
            title="Add New Contact/Provider"
            onClick={() => setEditContactState({ showModal: true })}>
            <span>Add New Contact/Provider</span>
          </Button>
          <span style={{ fontSize: ".75rem" }}>{`Total Contacts: ${contacts?.length}`}</span>
        </Space>
      </Flex>
      {loading && (
        <Flex justify="space-around" style={{ width: "100%", textAlign: "center" }}>
          <Spin tip="Loading, please wait..." />
        </Flex>
      )}
      {error && <ErrorViewer error={error} />}
      {!loading && (
        <Card styles={{ body: { padding: "0" } }} className="card-style">
          <Table<TContact>
            key={tableKey}
            columns={composeColumns(
              {
                onEditContact,
                onEditVTUser,
                onViewDictationInstructions
              },
              hasManageVTUsers,
              hasViewDictationInstructions,
              hasViewDictationInstructionsLocation
            )}
            dataSource={contacts.sort((a, b) => a.contactName.localeCompare(b.contactName))}
            pagination={{
              pageSize: pageSize,
              showSizeChanger: {
                showSearch: false
              },
              pageSizeOptions: [5, 10, 20, 50, 100],
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size || 10);
              }
            }}
            rowKey={"contactId"}
            className="table-style"
            components={{
              body: {
                cell: (props: any) => (
                  <td
                    {...props}
                    style={{
                      borderBottomWidth: "1px",
                      borderBottomColor: "#f0f0f0",
                      borderBottomStyle: "solid",
                      fontWeight: "500",
                    }}
                  />
                )
              }
            }}
            style={{ border: "none" }}
          />
        </Card>
      )}

      {editContactState?.showModal && (
        <AddOrUpdateProvider
          contact={editContactState.contact}
          onClose={closeEditContactModal}
        />
      )}
      {editVTUserState && editVTUserState.contact && (
        <AddOrUpdateVTUser contact={editVTUserState.contact} onClose={closeEditVTUserModal} />
      )}
      {viewDictationInstructions && viewDictationInstructions.contact && (
        <DictationInstructions
          contact={viewDictationInstructions.contact}
          onClose={closeViewDictationInstructionsModal}
        />
      )}
    </Card>
  );
};

export default ManageContacts;
